/**
 * 予算入力(管理者のみ)。事業部 × 科目 × 月のグリッド。
 * スプレッドシートからの貼り付けに対応する(タブ区切り・改行区切りをそのまま流し込める)。
 */
import { useCallback, useEffect, useMemo, useState, type ClipboardEvent } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import { fiscalYearOf, monthsOfFiscalYear, todayStr } from '@core/lib/date';
import { formatAmountInput, formatYen, parseAmount } from '@core/lib/money';
import { fetchBudgetsForYear, saveBudgets, type BudgetCell } from './budgetRepo';
import V8BudgetImportModal from './V8BudgetImportModal';
import { useIsMobile } from '@core/lib/useIsMobile';
import type { Account, DepartmentId } from '@core/lib/types';

/** 科目の区分(売上・原価・経費)の表示名 */
function plLabel(a: Account): string {
  return a.pl_line === 'revenue' ? '売上' : a.pl_line === 'cogs' ? '原価' : '経費';
}

/** 表示対象の科目(PLに乗るものだけ) */
function budgetAccounts(accounts: Account[]): Account[] {
  const order: Record<string, number> = { revenue: 0, cogs: 1, expense: 2 };
  return accounts
    .filter((a) => a.active && a.pl_line !== null)
    .sort(
      (a, b) =>
        (order[a.pl_line ?? 'z'] ?? 9) - (order[b.pl_line ?? 'z'] ?? 9) ||
        a.display_order - b.display_order,
    );
}

export default function BudgetPage() {
  const { user } = useAuth();
  const { departments, accounts, fiscal } = useMasters();
  const toast = useToast();
  /** スマホ(2026-09-07 指示): 科目×12ヶ月の表は横に収まらないため、科目ごとのカード(月は3列)にする */
  const isMobile = useIsMobile();

  const [departmentId, setDepartmentId] = useState<DepartmentId>(
    (departments[0]?.id ?? 'honbu') as DepartmentId,
  );
  const [fiscalYear, setFiscalYear] = useState(fiscalYearOf(todayStr(), fiscal.start_month));
  /** `${accountId}:${month}` → 入力中の文字列 */
  const [cells, setCells] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  // 2027年度予算 v8 のシートからの取り込み(2026-10-23 段階5・K7)。表に入れるだけで、保存は「保存」ボタン
  const [v8Open, setV8Open] = useState(false);

  const months = useMemo(
    () => monthsOfFiscalYear(fiscalYear, fiscal.start_month),
    [fiscalYear, fiscal.start_month],
  );
  const rows = useMemo(() => budgetAccounts(accounts), [accounts]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const budgets = await fetchBudgetsForYear(departmentId, fiscalYear);
      const next: Record<string, string> = {};
      for (const b of budgets) next[`${b.account_id}:${b.month}`] = String(b.amount);
      setCells(next);
      setDirty(false);
    } catch (e) {
      toast(e instanceof Error ? e.message : '予算を取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [departmentId, fiscalYear, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  function setCell(accountId: string, month: string, value: string) {
    setCells((prev) => ({ ...prev, [`${accountId}:${month}`]: value }));
    setDirty(true);
  }

  /**
   * スプレッドシートからの貼り付け。
   * 貼り付けた位置を左上として、タブ区切り=右方向(月)、改行=下方向(科目)に流し込む。
   */
  function handlePaste(e: ClipboardEvent<HTMLInputElement>, rowIndex: number, colIndex: number) {
    const text = e.clipboardData.getData('text/plain');
    if (!text.includes('\t') && !text.includes('\n')) return; // 単一セルは通常の貼り付けに任せる
    e.preventDefault();

    const lines = text.replace(/\r/g, '').split('\n').filter((l) => l.trim() !== '');
    const next = { ...cells };
    let filled = 0;
    let skipped = 0;

    lines.forEach((line, r) => {
      line.split('\t').forEach((raw, c) => {
        const account = rows[rowIndex + r];
        const month = months[colIndex + c];
        if (!account || !month) return;
        const value = raw.trim();
        if (value === '') {
          next[`${account.id}:${month}`] = '';
          return;
        }
        const amount = parseAmount(value);
        if (amount === null) {
          skipped++;
          return;
        }
        next[`${account.id}:${month}`] = String(amount);
        filled++;
      });
    });

    setCells(next);
    setDirty(true);
    toast(
      skipped > 0
        ? `${filled}件を貼り付けました(数値として読めない${skipped}件は無視)`
        : `${filled}件を貼り付けました`,
    );
  }

  async function save() {
    if (!user) return;
    const payload: BudgetCell[] = [];
    let invalid = 0;

    for (const account of rows) {
      for (const month of months) {
        const raw = (cells[`${account.id}:${month}`] ?? '').trim();
        const amount = raw === '' ? 0 : parseAmount(raw);
        if (amount === null) {
          invalid++;
          continue;
        }
        payload.push({ departmentId, fiscalYear, month, accountId: account.id, amount });
      }
    }

    if (invalid > 0) {
      toast(`金額として読めない入力が${invalid}件あります。修正してください`);
      return;
    }

    setBusy(true);
    try {
      await saveBudgets(payload, user.id);
      toast('予算を保存しました');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const monthTotal = (month: string) =>
    rows.reduce((sum, a) => sum + (parseAmount(cells[`${a.id}:${month}`] ?? '') ?? 0), 0);

  const rowTotal = (accountId: string) =>
    months.reduce((sum, m) => sum + (parseAmount(cells[`${accountId}:${m}`] ?? '') ?? 0), 0);

  return (
    <section className="screen" style={{ maxWidth: 1400 }}>
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          予算入力(事業部 × 科目 × 月)
          <select
            style={{ marginLeft: 'auto', padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', fontSize: 13 }}
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value as DepartmentId)}
          >
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <select
            style={{ padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', fontSize: 13 }}
            value={fiscalYear}
            onChange={(e) => setFiscalYear(Number(e.target.value))}
          >
            {[fiscalYear - 1, fiscalYear, fiscalYear + 1].map((y) => (
              <option key={y} value={y}>
                {y}年度
              </option>
            ))}
          </select>
          <button className="btn-line" onClick={() => setV8Open(true)} disabled={busy || loading}>
            v8 から取り込む
          </button>
          <button className="btn-green" style={{ marginTop: 0 }} onClick={() => void save()} disabled={busy || !dirty}>
            {busy ? '保存中…' : '保存'}
          </button>
        </h3>

        <p className="hint" style={{ marginBottom: 12 }}>
          スプレッドシートから範囲をコピーし、貼り付け先の左上のセルで <b>Ctrl + V</b> すると一括入力できます。
          空欄は0として扱います({fiscal.start_month}月開始・{fiscalYear}年度)。
        </p>

        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : isMobile ? (
          <div className="stack-cards">
            {rows.map((account, rowIndex) => (
              <div className="stack-card" key={account.id}>
                <div className="stack-card-head">
                  <b>{account.name}</b>
                  <span className="hint">{plLabel(account)}</span>
                  <span className="num" style={{ marginLeft: 'auto', fontWeight: 700 }}>
                    {formatYen(rowTotal(account.id))}
                  </span>
                </div>
                <div className="month-grid">
                  {months.map((month, colIndex) => (
                    <label key={month} className="month-cell">
                      <span>{Number(month.slice(5))}月</span>
                      <input
                        className="num"
                        inputMode="numeric"
                        value={cells[`${account.id}:${month}`] ?? ''}
                        onChange={(e) => setCell(account.id, month, formatAmountInput(e.target.value))}
                        onPaste={(e) => handlePaste(e, rowIndex, colIndex)}
                      />
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <div className="stack-card" style={{ background: '#FAFBF9' }}>
              <div className="stack-card-head">
                <b>月計</b>
                <span className="num" style={{ marginLeft: 'auto', fontWeight: 700 }}>
                  {formatYen(months.reduce((s, m) => s + monthTotal(m), 0))}
                </span>
              </div>
              <div className="month-grid">
                {months.map((m) => (
                  <div key={m} className="month-cell">
                    <span>{Number(m.slice(5))}月</span>
                    <div className="num" style={{ fontWeight: 700, color: 'var(--ink)' }}>{formatYen(monthTotal(m))}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div>
            <table className="tx-table">
              <thead>
                <tr>
                  <th style={{ position: 'sticky', left: 0, background: '#fff', minWidth: 130 }}>科目</th>
                  {months.map((m) => (
                    <th key={m} className="num">
                      {Number(m.slice(5))}月
                    </th>
                  ))}
                  <th className="num">年度計</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((account, rowIndex) => (
                  <tr key={account.id}>
                    <td style={{ position: 'sticky', left: 0, background: '#fff' }}>
                      {account.name}
                      <span className="hint"> {plLabel(account)}</span>
                    </td>
                    {months.map((month, colIndex) => (
                      <td key={month} style={{ padding: '3px 4px' }}>
                        <input
                          className="num"
                          style={{
                            width: 92,
                            padding: '5px 6px',
                            border: '1px solid var(--line)',
                            borderRadius: 4,
                            fontFamily: 'inherit',
                            fontSize: 13,
                          }}
                          value={cells[`${account.id}:${month}`] ?? ''}
                          onChange={(e) => setCell(account.id, month, formatAmountInput(e.target.value))}
                          onPaste={(e) => handlePaste(e, rowIndex, colIndex)}
                        />
                      </td>
                    ))}
                    <td className="num" style={{ fontWeight: 600 }}>
                      {formatYen(rowTotal(account.id))}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{ position: 'sticky', left: 0, background: '#fff', fontWeight: 700 }}>月計</td>
                  {months.map((m) => (
                    <td key={m} className="num" style={{ fontWeight: 700 }}>
                      {formatYen(monthTotal(m))}
                    </td>
                  ))}
                  <td className="num" style={{ fontWeight: 700 }}>
                    {formatYen(months.reduce((s, m) => s + monthTotal(m), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <p className="hint" style={{ marginTop: 10 }}>
          ここで入力した予算は、全社PL・本部PLの「予算」列と達成率に反映されます。
          保存キーは 事業部 × 年度 × 月 × 科目 のため、同じ枠を二重に登録することはありません。
          {dirty && <b> 未保存の変更があります。</b>}
        </p>
      </div>
      {v8Open && (
        <V8BudgetImportModal
          departmentName={departments.find((d) => d.id === departmentId)?.name ?? departmentId}
          fiscalYear={fiscalYear}
          months={months}
          accounts={accounts}
          budgetAccountIds={new Set(rows.map((r) => r.id))}
          onApply={(applied) => {
            // v8 の2つの科目①が同じ科目に当たるとき(「仕入」と「仕入高」など)は足し合わせる(上書きしない)
            const summed = new Map<string, number[]>();
            for (const r of applied) {
              const acc = summed.get(r.accountId) ?? Array(12).fill(0);
              r.amounts.forEach((a, i) => (acc[i] += a));
              summed.set(r.accountId, acc);
            }
            setCells((prev) => {
              const next = { ...prev };
              for (const [accountId, amounts] of summed) months.forEach((m, i) => (next[`${accountId}:${m}`] = amounts[i] ? String(amounts[i]) : ''));
              return next;
            });
            setDirty(true);
            toast(`${summed.size}科目を表に入れました。確かめて「保存」を押してください`);
          }}
          onClose={() => setV8Open(false)}
        />
      )}
    </section>
  );
}
